import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { AppContext } from '../../contexts/AppContext';
import CircularProgress from '@mui/material/CircularProgress';
import RadioGroup from '@mui/material/RadioGroup';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import Radio from '@mui/material/Radio';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import Close from '@mui/icons-material/Close';

interface ComparePlanActivityDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (activityId?: number) => void;
  stravaCode?: string | null;
  stravaError?: string | null;
  date: Date;
}

const ComparePlanActivityDialog: React.FC<ComparePlanActivityDialogProps> = ({
  open,
  onClose,
  onSubmit,
  stravaCode,
  stravaError,
  date
}) => {
  const intl = useIntl();

  const { stravaService } = React.useContext(AppContext);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const formatDistance = (meters: number) => {
    const km = meters / 1000;
    return `${km.toFixed(2)} km`;
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  useEffect(() => {
    if (!open) {
      setSelectedActivityId(null);
      setIsSubmitting(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    async function handleCallback() {
      setError(null);

      if (!stravaCode && !stravaError) return;

      if (stravaError) {
        setError("error.stravaAuthDeniedOrCancelled");
        return;
      }

      if (stravaCode) {
        try {
          const success = await stravaService.handleCallback(stravaCode);
          if (success) {
            setError(null);
          } else {
            setError("error.stravaFailedConnect");
          }
        } catch (err) {
          setError("error.stravaUnexpectedError");
        }
      }
    }

    if (!stravaService.isConnected) {
      handleCallback();
    }
  }, [stravaCode, stravaError, stravaService]);

  useEffect(() => {
    if (stravaService.isConnected && open) {
      setError(null);
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      stravaService.getActivitiesByDateRange(startOfDay, endOfDay);
    }
  }, [stravaService.isConnected, date, stravaService, open]);

  const handleActivitySelect = (activityId: number) => {
    setSelectedActivityId(activityId);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    onSubmit(selectedActivityId || undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormattedMessage id="plans.compareActivity" />
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {
          stravaService.isConnected ?
            <>
              <Typography variant="body1" marginBottom={2}>
                <FormattedMessage id="plans.compareActivityStep2" />
              </Typography>

              {stravaService.isLoading && !isSubmitting ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : stravaService.activities.length > 0 ? (
                <Box>
                  <RadioGroup
                    value={selectedActivityId || ''}
                    onChange={(e) => handleActivitySelect(Number(e.target.value))}
                  >
                    <List>
                      {stravaService.activities.map((activity) => (
                        <React.Fragment key={activity.id}>
                          <ListItem disablePadding>
                            <FormControlLabel
                              value={activity.id}
                              control={<Radio />}
                              label={
                                <Box ml={1}>
                                  <Typography variant="subtitle1">{activity.name}</Typography>
                                  <Box display="flex" gap={2}>
                                    <Typography variant="body2" color="text.secondary">
                                      {activity.type}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDistance(activity.distance)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDuration(activity.moving_time)}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              sx={{ width: '100%' }}
                            />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  </RadioGroup>
                </Box>
              ) : (
                <Typography color="text.secondary" align="center">
                  <FormattedMessage id="plans.noActivitiesFound" />
                </Typography>
              )}
            </>
            : <>
              <Typography variant="body1" marginBottom={2}>
                <FormattedMessage id="plans.compareActivityStep1" />
              </Typography>
              <LoadingButton
                onClick={() => stravaService.connect()}
                variant="contained"
                endIcon={
                  <img
                    src="/images/strava.svg"
                    alt="Strava"
                    style={{ height: 20, marginLeft: 8 }}
                  />
                }
                loading={stravaService.isLoading}
              >
                {intl.formatMessage({ id: 'plans.connectStrava' })}
              </LoadingButton>
            </>
        }
        {error && (
          <Typography variant="body2" color="error" marginTop={2}>
            {intl.formatMessage({ id: error })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="common.cancel" />
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          loading={isSubmitting}
          disabled={(stravaService.isConnected && !selectedActivityId) || isSubmitting}
        >
          <FormattedMessage id="action.ok" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ComparePlanActivityDialog;